import React, { useMemo, useState } from 'react';
import { Linking, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Card, Checkbox, HelperText, Text, TextInput, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useSchedule } from '../../contexts/ScheduleContext';
import { t, useLanguage } from '../../i18n';
import {
  ACADEMIC_CALENDAR_URL, CALENDAR_PRESETS, CLASS_PERIOD_TIMES, CalendarValidationError,
  buildCalendarIcs, buildCalendarSeries, buildGoogleCalendarUrl, calendarCourseKey,
  getCourseCalendarIssue, getDefaultCalendarRange, validateCalendarRange,
} from '../../utils/calendarExport';

function errorLabel(error: unknown): string {
  if (!(error instanceof CalendarValidationError)) return t('カレンダーを開けませんでした。もう一度お試しください。');
  switch (error.code) {
    case 'invalidDate': return t('日付を YYYY-MM-DD 形式で入力してください。');
    case 'reversedRange': return t('終了日は開始日以降にしてください。');
    case 'longRange': return t('登録期間は1年以内にしてください。');
    case 'missingName': return t('授業名がないため登録できません。');
    case 'noWeeklySlot': return t('曜日・時限未定、オンデマンド・集中講義は対象外です。');
    case 'noOccurrence': return t('この期間に授業の曜日がありません。');
  }
}

export default function CalendarExportScreen({ route }: any) {
  useLanguage();
  const theme = useTheme();
  const { userCourses } = useSchedule(); // Read only: never changes the saved timetable.
  const courses = useMemo(() => {
    const unique = new Map(userCourses.map(course => [calendarCourseKey(course), course]));
    return [...unique.values()].filter(course => !route.params?.courseSlotKey || calendarCourseKey(course) === route.params.courseSlotKey)
      .sort((a, b) => (a.dayOfWeek ?? 99) - (b.dayOfWeek ?? 99) || (a.period ?? 99) - (b.period ?? 99));
  }, [userCourses, route.params?.courseSlotKey]);
  const [range, setRange] = useState(() => getDefaultCalendarRange(courses));
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState('');
  const [actionError, setActionError] = useState('');
  const [exporting, setExporting] = useState(false);

  let rangeError = '';
  try { validateCalendarRange(range); } catch (error) { rangeError = errorLabel(error); }
  const previews = courses.map(course => {
    let series: ReturnType<typeof buildCalendarSeries> | null = null;
    let issue = getCourseCalendarIssue(course);
    if (!issue && !rangeError) {
      try { series = buildCalendarSeries(course, range); }
      catch (error) { issue = error instanceof CalendarValidationError ? error.code : 'noOccurrence'; }
    }
    return { course, series, issue, key: calendarCourseKey(course) };
  });
  const selectedSeries = previews.filter(item => item.series && !excludedKeys.has(item.key)).map(item => item.series!);
  const selectedCount = selectedSeries.reduce((sum, series) => sum + series.occurrences, 0);
  const days = ['', '月', '火', '水', '木', '金', '土', '日'];

  const openGoogle = async (series: NonNullable<typeof previews[number]['series']>) => {
    setActionError('');
    setStatus('');
    try {
      const url = buildGoogleCalendarUrl(series);
      if (Platform.OS === 'web') {
        // Synchronous user gesture: no async preparation that would block mobile popups.
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else await Linking.openURL(url);
      // Opening a draft is not proof that an event was saved.
      setStatus(t('Googleカレンダー側で日時・繰り返しを確認して「保存」を押してください。'));
    } catch (error) { setActionError(errorLabel(error)); }
  };

  const exportSelected = async () => {
    setExporting(true);
    setActionError('');
    setStatus('');
    try {
      const content = buildCalendarIcs(selectedSeries);
      const filename = `tufs-syla-${range.startDate}-${range.endDate}.ics`;
      if (Platform.OS === 'web') {
        const url = URL.createObjectURL(new Blob([content], { type: 'text/calendar;charset=utf-8' }));
        try {
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
      } else {
        const [fileSystem, sharing] = await Promise.all([import('expo-file-system'), import('expo-sharing')]);
        if (!fileSystem.cacheDirectory || !await sharing.isAvailableAsync()) throw new Error('Sharing unavailable');
        const path = fileSystem.cacheDirectory + filename;
        await fileSystem.writeAsStringAsync(path, content, { encoding: fileSystem.EncodingType.UTF8 });
        await sharing.shareAsync(path, { mimeType: 'text/calendar', UTI: 'public.calendar-event' });
      }
      setStatus(t('ファイルを作成しました。パソコン版Googleカレンダーの「設定 → インポート／エクスポート」から読み込めます。'));
    } catch { setActionError(t('ファイルを作成できませんでした。ブラウザ版でお試しください。')); }
    finally { setExporting(false); }
  };

  return (
    <ScrollView style={{ backgroundColor: theme.colors.background }} contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
      <View style={[styles.intro, { backgroundColor: theme.colors.primaryContainer }]}>
        <Ionicons name="calendar-outline" size={28} color={theme.colors.onPrimaryContainer} />
        <Text variant="titleMedium" style={{ color: theme.colors.onPrimaryContainer }}>{t('毎週の授業をカレンダーへ')}</Text>
        <Text style={{ color: theme.colors.onPrimaryContainer, lineHeight: 22 }}>{t('授業ごとのボタンでGoogleカレンダーの登録画面を開きます。最後にGoogle側で「保存」を押してください。アプリへのアクセス権の付与は不要です。')}</Text>
      </View>

      <Text variant="titleMedium">{t('登録期間')}</Text>
      <View style={styles.presetRow}>
        <Button mode="outlined" compact onPress={() => { setRange({ ...CALENDAR_PRESETS.spring2026 }); setStatus(''); }}>{t('2026年度 春学期')}</Button>
        <Button mode="outlined" compact onPress={() => { setRange({ ...CALENDAR_PRESETS.autumn2026 }); setStatus(''); }}>{t('2026年度 秋学期')}</Button>
      </View>
      <View style={styles.dateRow}>
        <TextInput label={t('開始日')} accessibilityLabel={t('開始日')} value={range.startDate} placeholder="YYYY-MM-DD" mode="outlined" style={styles.dateInput}
          onChangeText={startDate => { setRange(value => ({ ...value, startDate })); setStatus(''); }} autoCapitalize="none" autoCorrect={false} />
        <TextInput label={t('終了日')} accessibilityLabel={t('終了日')} value={range.endDate} placeholder="YYYY-MM-DD" mode="outlined" style={styles.dateInput}
          onChangeText={endDate => { setRange(value => ({ ...value, endDate })); setStatus(''); }} autoCapitalize="none" autoCorrect={false} />
      </View>
      <HelperText type="error" visible={!!rangeError}>{rangeError}</HelperText>
      <Text style={{ color: theme.colors.onSurfaceVariant, lineHeight: 22 }}>{t('日本時間（Asia/Tokyo）で登録します。開始日以降の最初の授業日から、終了日まで毎週繰り返します。')}</Text>
      <Button compact onPress={() => Linking.openURL(ACADEMIC_CALENDAR_URL).catch(() => setActionError(t('カレンダーを開けませんでした。もう一度お試しください。')))} style={styles.calendarSource}>{t('大学の学年暦を確認')}</Button>
      <View style={[styles.notice, { backgroundColor: theme.colors.surfaceVariant }]}>
        <Text style={{ lineHeight: 22 }}>{t('休講・祝日・長期休暇・補講日は自動調整しません。学期途中までの授業は期間を変更してください。登録後の時間割変更も自動同期されません。')}</Text>
        <Text style={{ lineHeight: 22 }}>{t('メモ・出欠・成績・メールアドレスは送りません。授業名・教室・担当教員・シラバスURLのみを含めます。')}</Text>
        <Text style={{ lineHeight: 22 }}>{t('同じ授業をもう一度保存すると重複する場合があります。Google側の登録先も確認してください。')}</Text>
      </View>

      <Text variant="titleMedium">{t('登録する授業')}</Text>
      {!courses.length && <Text style={styles.empty}>{t('時間割に授業が登録されていません')}</Text>}
      {previews.map(({ course, key, series, issue }) => (
        <Card key={key} mode="outlined" style={styles.courseCard}>
          <Card.Content>
            <Checkbox.Item label={course.name} status={!issue && !excludedKeys.has(key) ? 'checked' : 'unchecked'} disabled={!!issue} position="leading"
              onPress={() => setExcludedKeys(previous => { const next = new Set(previous); next.has(key) ? next.delete(key) : next.add(key); return next; })}
              style={styles.checkbox} labelStyle={styles.courseName} />
            {!issue && <Text style={styles.courseMeta}>{t('毎週{0}曜日・{1}限　{2}〜{3}', [t(days[course.dayOfWeek]), course.period, CLASS_PERIOD_TIMES[course.period].start, CLASS_PERIOD_TIMES[course.period].end])}</Text>}
            {!!course.room && <Text style={styles.courseMeta}>{t('教室：')}{course.room}</Text>}
            {series && <Text style={styles.courseMeta}>{t('{0} 〜 {1}・全{2}回', [series.firstDate, series.lastDate, series.occurrences])}</Text>}
            {!!issue && <HelperText type="info">{errorLabel(new CalendarValidationError(issue))}</HelperText>}
            <Button mode="contained-tonal" disabled={!series || excludedKeys.has(key)} onPress={() => series && openGoogle(series)} style={styles.googleButton}
              icon={({ size, color }) => <Ionicons name="open-outline" size={size} color={color} />}>
              {t('Googleカレンダーに追加')}
            </Button>
          </Card.Content>
        </Card>
      ))}

      {!!courses.length && (
        <View style={[styles.notice, { backgroundColor: theme.colors.surfaceVariant }]}>
          <Text variant="titleMedium">{t('まとめて追加したい場合')}</Text>
          <Text style={{ lineHeight: 22 }}>{t('選択した授業を1つのカレンダーファイルにできます。Googleカレンダーへの一括読み込みはパソコンから行ってください。')}</Text>
          <Text>{t('{0}科目・計{1}回を選択中', [selectedSeries.length, selectedCount])}</Text>
          <Button mode="outlined" disabled={!selectedSeries.length || exporting} loading={exporting} onPress={exportSelected}>
            {t('選択した授業を保存（.ics）')}
          </Button>
        </View>
      )}
      {!!status && <Text accessibilityRole="alert" style={[styles.feedback, { color: theme.colors.primary }]}>{status}</Text>}
      {!!actionError && <Text accessibilityRole="alert" style={[styles.feedback, { color: theme.colors.error }]}>{actionError}</Text>}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: { padding: 16, paddingBottom: 48, gap: 12, maxWidth: 720, width: '100%', alignSelf: 'center' },
  intro: { padding: 16, borderRadius: 16, gap: 10 },
  presetRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  dateRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  dateInput: { flex: 1, minWidth: 140 },
  calendarSource: { alignSelf: 'flex-start' },
  notice: { padding: 14, borderRadius: 12, gap: 10 },
  courseCard: { marginBottom: 2 },
  checkbox: { paddingHorizontal: 0 },
  courseName: { textAlign: 'left', fontWeight: '600', flexShrink: 1 },
  courseMeta: { marginBottom: 6, lineHeight: 22 },
  googleButton: { marginTop: 8 },
  feedback: { lineHeight: 23, paddingVertical: 8 },
  empty: { paddingVertical: 24 },
});
